import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { BRAND_IMG } from "../components/SiteBackground";
import {
  Compass, ArrowRight, MessageSquare, ScanLine, Ruler, TrendingUp,
  Hammer, Tag, FileText, Bookmark, Check,
} from "lucide-react";

const PROOF = [
  { icon: Hammer, title: "Design & Build", desc: "Describe anything — get an image, real dimensions, a cut list, and a full build plan with material costs." },
  { icon: Tag, title: "Products & Services", desc: "Turn what you make into a listing. AI sharpens the title, price and description, and hands you copy-paste marketplace packets." },
  { icon: FileText, title: "Invoices", desc: "Package your work as a real offer and send a clean PDF invoice when you're ready to get paid." },
];

const INSIDE = [
  "Talk to the Mentor", "Scan & Measure", "Trends", "Guided paths & weekly steps",
  "Design & Build It", "Products & Services + AI listing improve + marketplace packets",
  "Invoices with PDF download", "Saved paths, pins, estimates & sessions",
];

const STEPS = [
  { n: "1", t: "Start with a chat, a photo scan, or a quick intake." },
  { n: "2", t: "Get a path with this week's first step — sized to your time and cash." },
  { n: "3", t: "Design it, package it as a product or service, then invoice when you're ready." },
];

function PublicHeader() {
  const nav = useNavigate();
  return (
    <header className="sticky top-0 z-20 backdrop-blur border-b" style={{ background: "rgba(255,255,255,0.85)", borderColor: "var(--border-subtle)" }} data-testid="public-header">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <button onClick={() => nav("/")} className="flex items-center gap-2" data-testid="header-logo">
          <Compass className="w-5 h-5" />
          <span className="font-head font-extrabold text-lg">Path Mentor AI</span>
        </button>
        <nav className="flex items-center gap-3 sm:gap-5 text-sm">
          <Link to="/pricing" className="underline-offset-4 hover:underline" data-testid="header-pricing-link">Pricing</Link>
          <Link to="/login" className="underline-offset-4 hover:underline" data-testid="header-signin-link">Sign in</Link>
        </nav>
      </div>
    </header>
  );
}

export default function Landing() {
  const nav = useNavigate();
  const { guest } = useAuth();
  const glass = { background: "rgba(255,255,255,0.72)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", borderColor: "rgba(0,0,0,0.08)" };
  const heroScrim = { background: "rgba(8,11,20,0.5)", backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)", borderRadius: "20px", padding: "28px 26px", border: "1px solid rgba(255,255,255,0.08)" };
  const textShadow = "0 2px 14px rgba(0,0,0,0.55)";

  const startFree = async () => {
    try { await guest(); nav("/scan"); }
    catch { toast.error("Could not start a free preview"); }
  };

  const googleSignIn = () => {
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen relative" style={{ background: "#0b1020", color: "var(--text)" }} data-testid="landing-page">
      <div aria-hidden className="fixed inset-0 pointer-events-none" style={{ zIndex: 0, backgroundImage: `url(${BRAND_IMG})`, backgroundRepeat: "no-repeat", backgroundPosition: "center", backgroundSize: "cover" }} />
      <div className="relative" style={{ zIndex: 1 }}>
      <PublicHeader />

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-16 pb-14">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div style={heroScrim}>
            <div className="label-mono mb-4" style={{ color: "#c7d2fe" }} data-testid="hero-eyebrow">AI business mentor for any size idea</div>
            <h1 className="font-head font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05]" style={{ color: "#ffffff", textShadow }} data-testid="hero-headline">
              From idea → plan → offer → invoice.
            </h1>
            <p className="mt-5 text-base sm:text-lg max-w-xl" style={{ color: "rgba(255,255,255,0.9)", textShadow: "0 1px 8px rgba(0,0,0,0.5)" }} data-testid="hero-subhead">
              Path Mentor helps you turn any business idea — tiny side hustle or bigger company — into a clear plan,
              a build path, a sellable offer, and a bill you can send. No hype. No promised income.
              Honest “don't do this” calls when something's a waste.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button onClick={startFree} className="pm-btn flex items-center gap-2" data-testid="cta-start-free">
                Start free — 1 scan &amp; 1 measure <ArrowRight className="w-4 h-4" />
              </button>
              <Link to="/pricing" className="pm-btn-ghost flex items-center gap-2" style={{ color: "#ffffff", borderColor: "rgba(255,255,255,0.5)", background: "rgba(255,255,255,0.08)" }} data-testid="cta-see-pricing">See pricing</Link>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-3 text-sm" style={{ color: "rgba(255,255,255,0.9)" }}>
              <Link to="/login" className="underline underline-offset-4" data-testid="cta-signin">Sign in</Link>
              <span style={{ color: "rgba(255,255,255,0.4)" }}>·</span>
              <button onClick={googleSignIn} className="underline underline-offset-4" data-testid="cta-google">Continue with Google</button>
            </div>
          </div>

          {/* feature glance */}
          <div className="grid grid-cols-2 gap-3" data-testid="hero-feature-grid">
            {[
              { icon: MessageSquare, t: "Talk to the Mentor" },
              { icon: ScanLine, t: "Scan" },
              { icon: Ruler, t: "Measure" },
              { icon: TrendingUp, t: "Trends" },
              { icon: Compass, t: "Guided paths" },
              { icon: Hammer, t: "Design & Build It" },
              { icon: Tag, t: "Products & Services" },
              { icon: FileText, t: "Invoices (PDF)" },
              { icon: Bookmark, t: "Saved" },
            ].map(({ icon: Icon, t }) => (
              <div key={t} className="pm-card p-4 flex items-center gap-2.5" style={glass}>
                <Icon className="w-4 h-4 shrink-0" />
                <span className="text-sm font-medium">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Proof tiles */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="proof-tiles">
        <div className="grid md:grid-cols-3 gap-4">
          {PROOF.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="pm-card p-6" style={glass} data-testid={`proof-${title.split(" ")[0].toLowerCase()}`}>
              <div className="flex items-center gap-2 mb-2"><Icon className="w-5 h-5" /><span className="font-head font-bold text-lg">{title}</span></div>
              <p className="text-sm" style={{ color: "var(--text-2)" }}>{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12" data-testid="how-it-works">
        <h2 className="font-head font-bold text-2xl sm:text-3xl mb-6" style={{ color: "#ffffff", textShadow }}>How it works</h2>
        <div className="grid md:grid-cols-3 gap-4">
          {STEPS.map((s) => (
            <div key={s.n} className="pm-card p-6" style={glass}>
              <div className="font-head font-extrabold text-3xl" style={{ color: "var(--earn)" }}>{s.n}</div>
              <p className="text-sm mt-2" style={{ color: "var(--text-2)" }}>{s.t}</p>
            </div>
          ))}
        </div>
      </section>

      {/* What's inside membership */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="membership">
        <div className="pm-card p-6 sm:p-8" style={glass}>
          <h2 className="font-head font-bold text-2xl sm:text-3xl mb-1">What's inside membership</h2>
          <p className="text-sm mb-5" style={{ color: "var(--text-2)" }}>One membership, the full business stack.</p>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2.5">
            {INSIDE.map((f) => (
              <div key={f} className="flex items-start gap-2 text-sm"><Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "var(--earn)" }} /> {f}</div>
            ))}
          </div>
          <div className="mt-6"><Link to="/pricing" className="pm-btn inline-flex items-center gap-2">See pricing <ArrowRight className="w-4 h-4" /></Link></div>
        </div>
      </section>

      {/* Tone */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-wrap gap-2" data-testid="tone">
          {["No stock picking.", "No hype.", "No promised income.", "We tell you when something is a waste."].map((t) => (
            <span key={t} className="badge" style={{ background: "var(--surface-subtle)" }}>{t}</span>
          ))}
        </div>
      </section>

      {/* Footer CTA */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14" data-testid="footer-cta">
        <div className="pm-card p-8 text-center" style={glass}>
          <h2 className="font-head font-extrabold text-2xl sm:text-3xl">Ready when you are.</h2>
          <p className="text-sm mt-2 mb-6" style={{ color: "var(--text-2)" }}>Try 1 free scan or measure — upgrade when you want the full stack.</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <button onClick={startFree} className="pm-btn flex items-center gap-2" data-testid="footer-start-free">Start free — 1 scan &amp; 1 measure <ArrowRight className="w-4 h-4" /></button>
            <Link to="/pricing" className="pm-btn-ghost">See pricing</Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t" style={{ borderColor: "var(--border-subtle)" }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 flex flex-wrap items-center gap-x-6 gap-y-2 label-mono">
          <span className="flex items-center gap-2"><Compass className="w-4 h-4" /> Path Mentor AI</span>
          <Link to="/pricing" className="underline" data-testid="footer-pricing-link">Pricing</Link>
          <Link to="/login" className="underline">Sign in</Link>
          <a href="/privacy" className="underline" data-testid="footer-privacy-link">Privacy Policy</a>
          <a href="mailto:pathmentorai@gmail.com?subject=Path%20Mentor%20Support" className="underline" data-testid="footer-support-link">Contact support</a>
          <a href="tel:+18575203500" className="underline" data-testid="footer-support-phone">(857) 520-3500</a>
        </div>
      </footer>
      </div>
    </div>
  );
}

export { PublicHeader };
